import React, { useState, useEffect, useCallback } from 'react';
import { Intervention, Campaign, Site, Technicentre, Prestation } from '../../types';
import EnginStatusManager from './EnginStatusManager';

interface InterventionModalProps {
  show: boolean;
  intervention?: Intervention;
  campaigns: Campaign[];
  sites: Site[];
  technicentres: Technicentre[];
  prestations: Prestation[];
  isGOF: boolean;
  onSave: (intervention: Partial<Intervention>) => void;
  onClose: () => void;
}

// ===== Composant InterventionModal =====
const InterventionModal: React.FC<InterventionModalProps> = ({
  show,
  intervention,
  campaigns,
  sites,
  technicentres,
  prestations,
  isGOF,
  onSave,
  onClose,
}) => {
  const [formData, setFormData] = useState<Partial<Intervention>>({});
  const MAX_ENGINE_COUNT = 4;

  const getRefusedEnginesFromComment = (comment?: string): string[] => {
    if (!comment) return [];
    const match = comment.match(/Engins refus[^:]*:\s*\[([^\]]+)\]/i);
    if (!match) return [];

    return match[1]
      .split(/[,;|]/)
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const mergeEngineNumbers = (engines: Array<string | undefined | null>, comment?: string): string[] => {
    const merged: string[] = [];

    [...engines, ...getRefusedEnginesFromComment(comment)].forEach((engine) => {
      const normalized = (engine || '').trim();
      if (!normalized) return;
      if (merged.includes(normalized)) return;
      if (merged.length >= MAX_ENGINE_COUNT) return;
      merged.push(normalized);
    });

    return merged;
  };

  useEffect(() => {
    if (!show) {

      setFormData({});
      return;
    }

    if (intervention) {
      const formatForInput = (dateString: string) => {
        if (!dateString || dateString === 'null' || dateString === 'undefined') return '';
        try {
          if (dateString.includes(' ') && !dateString.includes('T')) {
            return dateString.replace(' ', 'T').slice(0, 16);
          }

          const date = new Date(dateString);
          if (isNaN(date.getTime())) return '';

          const options: Intl.DateTimeFormatOptions = {
            timeZone: 'Europe/Paris',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          };

          const parts = new Intl.DateTimeFormat('fr-FR', options).formatToParts(date);
          const year = parts.find(p => p.type === 'year')?.value;
          const month = parts.find(p => p.type === 'month')?.value;
          const day = parts.find(p => p.type === 'day')?.value;
          const hour = parts.find(p => p.type === 'hour')?.value;
          const minute = parts.find(p => p.type === 'minute')?.value;

          return `${year}-${month}-${day}T${hour}:${minute}`;
        } catch {
          return '';
        }
      };

      const formatDateOnly = (dateString: string) => {
        if (!dateString || dateString === 'null' || dateString === 'undefined') return '';
        try {
          const date = new Date(dateString);
          if (isNaN(date.getTime())) return '';

          const options: Intl.DateTimeFormatOptions = {
            timeZone: 'Europe/Paris',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          };

          const parts = new Intl.DateTimeFormat('fr-FR', options).formatToParts(date);
          const year = parts.find(p => p.type === 'year')?.value;
          const month = parts.find(p => p.type === 'month')?.value;
          const day = parts.find(p => p.type === 'day')?.value;

          return `${year}-${month}-${day}`;
        } catch {
          return '';
        }
      };

      const formattedData = {
        ...intervention,
        disponibilite_a_partir: formatForInput(intervention.disponibilite_a_partir),
        depart_pour_mission: formatForInput(intervention.depart_pour_mission),
        date_intervention: formatDateOnly(intervention.date_intervention || ''),
      };

      const currentEngines = [intervention.engin_1, intervention.engin_2, intervention.engin_3, intervention.engin_4].filter(Boolean);
      const refusedEngines: string[] = [];

      if (intervention.commentaires) {
        const refusalPattern = /Engins refusés:\s*([^\]]+)/;
        const match = intervention.commentaires.match(refusalPattern);
        if (match) {
          const refusedSection = match[1];
          const enginesWithReasons = refusedSection.split('|');
          enginesWithReasons.forEach(item => {
            const engineName = item.split(':')[0].trim();
            if (engineName && !currentEngines.includes(engineName)) {
              refusedEngines.push(engineName);
            }
          });
        }
      }

      const allEngines = mergeEngineNumbers(currentEngines, intervention.commentaires);
      if (allEngines.length > 0) {
        formattedData.engin_1 = allEngines[0] || '';
        formattedData.engin_2 = allEngines[1] || '';
        formattedData.engin_3 = allEngines[2] || '';
        formattedData.engin_4 = allEngines[3] || '';
      }

      setFormData(formattedData);
    } else {

      setFormData({});
    }
  }, [show, intervention, isGOF]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleInputChange = (field: keyof Intervention, value: any) => {
    if (field === 'disponibilite_a_partir' || field === 'depart_pour_mission') {
      if (value && value.includes('T')) {

        const yearMatch = value.match(/^(\d+)-/);
        if (yearMatch && yearMatch[1].length > 4) {

          value = value.replace(/^(\d{4})\d*-/, '$1-');
        }
        const mysqlFormat = value.replace('T', ' ') + ':00';
        setFormData(prev => ({ ...prev, [field]: mysqlFormat }));
        return;
      }
    }

    if (field === 'date_intervention') {

      if (value) {
        const yearMatch = value.match(/^(\d+)-/);
        if (yearMatch && yearMatch[1].length > 4) {
          value = value.replace(/^(\d{4})\d*-/, '$1-');
        }
      }
    }

    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const isAnnulationChecked = () => {
    return formData.annulation_client === 1 || formData.annulation_client === true;
  };

  const handleAnnulationChange = (checked: boolean) => {
    if (isGOF) {
      setFormData(prev => ({ ...prev, annulation_client: checked ? 1 : 0 }));
      return;
    }

    if (checked) {
      setFormData(prev => ({ ...prev, annulation_client: 1 }));
    } else if (isAnnulationChecked()) {
      setFormData(prev => ({ ...prev, annulation_client: 1 }));
    } else {
      setFormData(prev => ({ ...prev, annulation_client: 0 }));
    }
  };

  const handleStatusUpdate = useCallback((statusData: {
    gps: Record<string, string>;
    rdv: Record<string, string>;
    realise: Record<string, string>;
    validation: Record<string, string>;
    commentaires_gps: Record<string, string>;
    commentaires_rdv: Record<string, string>;
    commentaires_realise: Record<string, string>;
    commentaires_validation: Record<string, string>;
  }) => {
    setFormData(prev => ({
      ...prev,
      veille_gps_donnees: JSON.stringify(statusData.gps),
      rdv_fait_donnees: JSON.stringify(statusData.rdv),
      realise_donnees: JSON.stringify(statusData.realise),
      validation_donnees: JSON.stringify(statusData.validation),
      commentaires_gps_donnees: JSON.stringify(statusData.commentaires_gps),
      commentaires_rdv_donnees: JSON.stringify(statusData.commentaires_rdv),
      commentaires_realise_donnees: JSON.stringify(statusData.commentaires_realise),
      commentaires_validation_donnees: JSON.stringify(statusData.commentaires_validation)
    }));
  }, []);

  if (!show) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content modal-lg" style={{maxHeight: '90vh'}} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-gray-800 flex items-center">
            <i className="fas fa-plus-circle mr-3 text-blue-600"></i>
            <span>{intervention?.id ? 'Modifier intervention' : 'Nouvelle intervention'}</span>
          </h3>
          <button onClick={onClose} className="modal-close text-gray-400 hover:text-gray-600 text-2xl">
            <i className="fas fa-times"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" style={{maxHeight: 'calc(90vh - 8rem)', overflowY: 'auto', paddingRight: '0.5rem'}}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-flag mr-1"></i> Campagne <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.campagne_nom || ''}
                onChange={(e) => handleInputChange('campagne_nom', e.target.value)}
                required
                className="form-input"
              >
                <option value="">Sélectionner une campagne</option>
                {campaigns.map((campaign) => (
                  <option key={campaign.id} value={campaign.nom}>
                    {campaign.nom}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-map-marker-alt mr-1"></i> Lieu Intervention <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.site_nom || ''}
                onChange={(e) => handleInputChange('site_nom', e.target.value)}
                required
                className="form-input"
              >
                <option value="">Sélectionner un lieu</option>
                {sites.map((site) => (
                  <option key={site.id} value={site.nom}>
                    {site.nom}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-comment mr-1"></i> Lieu dit (commentaire)
              </label>
              <textarea
                value={formData.lieu_dit || ''}
                onChange={(e) => handleInputChange('lieu_dit', e.target.value)}
                rows={2}
                className="form-input"
                placeholder="Précisions sur le lieu d'intervention..."
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-cogs mr-1"></i> Prestation demandée <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.prestation_nom || ''}
                onChange={(e) => handleInputChange('prestation_nom', e.target.value)}
                required
                className="form-input"
              >
                <option value="">Sélectionner une prestation</option>
                {prestations.map((prestation) => (
                  <option key={prestation.id} value={prestation.nom}>
                    {prestation.nom}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-calendar-alt mr-1"></i> Dispo à partir du <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                value={formData.disponibilite_a_partir || ''}
                onChange={(e) => handleInputChange('disponibilite_a_partir', e.target.value)}
                max="9999-12-31T23:59"
                required
                className="form-input"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-user mr-1"></i> Contact <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.contact || ''}
                onChange={(e) => handleInputChange('contact', e.target.value)}
                required
                className="form-input"
                placeholder="Ex: Ludovic BORN 05.26.91.35.70"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-hashtag mr-1"></i> N° ELAN
              </label>
              <input
                type="text"
                value={formData.numero_elan || ''}
                onChange={(e) => handleInputChange('numero_elan', e.target.value)}
                className="form-input"
                placeholder="Ex: I24-1411"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-clock mr-1"></i> Départ pour mission le <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                value={formData.depart_pour_mission || ''}
                onChange={(e) => handleInputChange('depart_pour_mission', e.target.value)}
                min={formData.disponibilite_a_partir || undefined}
                max="9999-12-31T23:59"
                required
                className="form-input"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-building mr-1"></i> Technicentre
              </label>
              <select
                value={formData.technicentre_nom || ''}
                onChange={(e) => handleInputChange('technicentre_nom', e.target.value)}
                className={`form-input ${!isGOF ? 'bg-gray-400 text-gray-600 cursor-not-allowed' : ''}`}
                disabled={!isGOF}
              >
                <option value="">Sélectionner un technicentre</option>
                {technicentres.map((technicentre) => (
                  <option key={technicentre.id} value={technicentre.nom}>
                    {technicentre.nom}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Annulation Client - uniquement en modification, pas en création/duplication */}
          {intervention?.id && (
            <div className="border-t pt-4">
              <label className={`flex items-center space-x-3 ${
                !isGOF && isAnnulationChecked() ? 'cursor-not-allowed' : 'cursor-pointer'
              }`}>
                <input
                  type="checkbox"
                  checked={isAnnulationChecked()}
                  onChange={(e) => handleAnnulationChange(e.target.checked)}
                  disabled={!isGOF && isAnnulationChecked()}
                  className={`form-checkbox h-5 w-5 text-orange-600 rounded focus:ring-orange-500 ${
                    !isGOF && isAnnulationChecked() ? 'opacity-60 cursor-not-allowed' : ''
                  }`}
                />
                <span className={`text-sm font-semibold ${
                  !isGOF && isAnnulationChecked() ? 'text-gray-400' : 'text-gray-700'
                }`}>
                  <i className="fas fa-ban mr-2 text-orange-600"></i>
                  Annulation Client
                </span>
              </label>
              <p className="text-xs text-gray-500 ml-8 mt-1">
                Cocher cette case si l'intervention est annulée par le client. La gestion par engin sera désactivée.
                {!isGOF && isAnnulationChecked() && (
                  <>
                    <br />
                    <span className="text-orange-600 font-medium">
                      ⚠️ Une fois cochée, seul un GOF peut décocher cette case.
                    </span>
                  </>
                )}
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <i className="fas fa-train mr-1"></i> Numéros d'engins
            </label>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <input
                type="text"
                value={formData.engin_1 || ''}
                onChange={(e) => handleInputChange('engin_1', e.target.value)}
                className="form-input"
                placeholder="Engin 1"
              />
              <input
                type="text"
                value={formData.engin_2 || ''}
                onChange={(e) => handleInputChange('engin_2', e.target.value)}
                className="form-input"
                placeholder="Engin 2"
              />
              <input
                type="text"
                value={formData.engin_3 || ''}
                onChange={(e) => handleInputChange('engin_3', e.target.value)}
                className="form-input"
                placeholder="Engin 3"
              />
              <input
                type="text"
                value={formData.engin_4 || ''}
                onChange={(e) => handleInputChange('engin_4', e.target.value)}
                className="form-input"
                placeholder="Engin 4"
              />
            </div>
          </div>

          {isGOF && (
            <div className="space-y-6 border-t pt-6">
              {/* Détails de l'intervention pour validation - uniquement en modification */}
              {intervention?.id && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h4 className="text-sm font-bold text-blue-900 mb-3 flex items-center">
                    <i className="fas fa-info-circle mr-2"></i>
                    Détails de l'intervention à valider
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                    <div>
                      <span className="font-semibold text-blue-800">Prestation:</span>
                      <span className="ml-2 text-blue-900">{formData.prestation_nom || '-'}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-blue-800">Contact:</span>
                      <span className="ml-2 text-blue-900">{formData.contact || '-'}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-blue-800">Campagne:</span>
                      <span className="ml-2 text-blue-900">{formData.campagne_nom || '-'}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-blue-800">N° ELAN:</span>
                      <span className="ml-2 text-blue-900">{formData.numero_elan || '-'}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-blue-800">Lieu:</span>
                      <span className="ml-2 text-blue-900">{formData.site_nom || '-'}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-blue-800">Dispo:</span>
                      <span className="ml-2 text-blue-900">
                        {formData.disponibilite_a_partir ?
                          new Date(formData.disponibilite_a_partir).toLocaleString('fr-FR', {
                            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                          }) : '-'}
                      </span>
                    </div>
                    <div>
                      <span className="font-semibold text-blue-800">Départ:</span>
                      <span className="ml-2 text-blue-900">
                        {formData.depart_pour_mission ?
                          new Date(formData.depart_pour_mission).toLocaleString('fr-FR', {
                            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                          }) : '-'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="fas fa-calendar-check mr-1"></i> Date d'intervention SR
                  </label>
                  <input
                    type="date"
                    value={formData.date_intervention || ''}
                    onChange={(e) => handleInputChange('date_intervention', e.target.value)}
                    min={(() => {
                      if (!formData.disponibilite_a_partir) return undefined;
                      try {
                        const date = new Date(formData.disponibilite_a_partir);
                        return isNaN(date.getTime()) ? undefined : date.toISOString().split('T')[0];
                      } catch {
                        return undefined;
                      }
                    })()}
                    max={(() => {
                      if (!formData.depart_pour_mission) return undefined;
                      try {
                        const date = new Date(formData.depart_pour_mission);
                        return isNaN(date.getTime()) ? undefined : date.toISOString().split('T')[0];
                      } catch {
                        return undefined;
                      }
                    })()}
                    className="form-input"
                  />
                  {formData.disponibilite_a_partir && formData.depart_pour_mission && (
                    <p className="text-xs text-gray-500 mt-1">
                      <i className="fas fa-exclamation-triangle mr-1"></i>
                      Date doit être entre le {new Date(formData.disponibilite_a_partir).toLocaleDateString('fr-FR')}
                      {' '}et le {new Date(formData.depart_pour_mission).toLocaleDateString('fr-FR')}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="fas fa-comment mr-1"></i> Commentaire général
                  </label>
                  <textarea
                    value={formData.commentaires || ''}
                    onChange={(e) => handleInputChange('commentaires', e.target.value)}
                    rows={2}
                    className="form-input"
                    placeholder="Commentaire général de l'intervention..."
                  />
                </div>
              </div>

              {(intervention?.id && Object.keys(formData).length > 0) && (() => {
                const currentEngines = [formData.engin_1, formData.engin_2, formData.engin_3, formData.engin_4].filter(Boolean) as string[];
                const refusedEngines: string[] = [];

                if (formData.commentaires) {
                  const refusalPattern = /Engins refusés:\s*([^\]]+)/;
                  const match = formData.commentaires.match(refusalPattern);
                  if (match) {
                    const refusedSection = match[1];
                    const enginesWithReasons = refusedSection.split('|');
                    enginesWithReasons.forEach(item => {
                      const engineName = item.split(':')[0].trim();
                      if (engineName && !currentEngines.includes(engineName)) {
                        refusedEngines.push(engineName);
                      }
                    });
                  }
                }

                const allEngines = mergeEngineNumbers(currentEngines, formData.commentaires);

                return (
                  <EnginStatusManager
                    key={`engin-status-${intervention?.id || 'new'}`}
                    engins={allEngines}
                    formData={formData}
                    onUpdateStatus={handleStatusUpdate}
                    disabled={isAnnulationChecked()}
                  />
                );
              })()}
            </div>
          )}

          <div className="flex gap-4 pt-4">
            <button type="submit" className="flex-1 btn btn-success">
              <i className="fas fa-save mr-2"></i> Enregistrer
            </button>
            <button type="button" onClick={onClose} className="btn btn-cancel ml-auto">
              <i className="fas fa-times mr-2"></i> Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InterventionModal;
